import React, { useId, useMemo, useState } from 'react';
import { BookOpen, Check, Info, Lightbulb, Plus, Search, X } from 'lucide-react';
import { Button } from '../Button';
import {
  ART_TITEL,
  DOKUMENTE,
  GRUPPEN_TITEL,
  LIEFERANT_TITEL,
  ergaenzeDokumente,
  findeDokument,
  normalisiere,
} from '../../services/mitgeltendeDokumente';
import type { DokumentArt, DokumentGruppe, MitgeltendesDokument } from '../../services/mitgeltendeDokumente';
import { bedingungsHinweis, dokumentSchluessel, istBedingt, vorschlaegeFuerSchritt } from './unterlagenErgaenzen';
import type { SchrittQuelle } from './unterlagenErgaenzen';

// Bearbeitet die mitgeltenden Dokumente eines Arbeitsschritts (Spalte 3 im
// Plan, unter den Vorschriften). Gespeichert wird nur der Name (Langform);
// Erklärungen, Kurzformen und Vorschläge kommen aus dem Katalog
// `services/mitgeltendeDokumente`. Freie Einträge bleiben möglich.
//
// Genutzt in der Detailansicht (`darstellung="karte"`) und im Dialog
// „Arbeitsschritt anlegen/bearbeiten“ (`darstellung="feld"`).

interface MitgeltendeDokumenteEditorProps {
  dokumente: string[];
  onChange: (dokumente: string[]) => void;
  /** Der Schritt, wie er gerade bearbeitet wird — Grundlage der Vorschläge. */
  schritt: Omit<SchrittQuelle, 'mitgeltende_dokumente'>;
  titel?: string;
  icon?: React.ReactNode;
  /** 'karte' = eigene Karte (Detailansicht), 'feld' = Formularfeld (Dialog). */
  darstellung?: 'karte' | 'feld';
  /** Automatisch vorbelegte Einträge — werden als „vorgeschlagen“ markiert. */
  vorbelegt?: string[];
}

const ART_FARBE: Record<DokumentArt, string> = {
  A: 'bg-sky-50 text-sky-700 border-sky-200',
  B: 'bg-violet-50 text-violet-700 border-violet-200',
  C: 'bg-slate-100 text-slate-500 border-slate-200',
};

/** Nur Art A und B gehören je Arbeitsschritt in den Plan; Art C steht einmal als Grundsatz. */
const AUSWAHL: MitgeltendesDokument[] = DOKUMENTE.filter(d => d.art !== 'C');

const ArtMarke: React.FC<{ art?: DokumentArt }> = ({ art }) =>
  art ? (
    <span
      title={`Art ${art}: ${ART_TITEL[art]}`}
      className={`shrink-0 mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded border text-[10px] font-bold ${ART_FARBE[art]}`}
    >
      {art}
    </span>
  ) : (
    <span
      title="Freier Eintrag (nicht im Katalog)"
      className="shrink-0 mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded border border-dashed border-slate-300 text-[10px] text-slate-400"
    >
      –
    </span>
  );

const ErklaerungsZeile: React.FC<{ titel: string; children: React.ReactNode; className?: string }> = ({ titel, children, className = '' }) => (
  <div className={`sm:grid sm:grid-cols-[8.5rem_1fr] sm:gap-2 ${className}`}>
    <dt className="font-semibold text-slate-500">{titel}</dt>
    <dd className="break-words">{children}</dd>
  </div>
);

/** Aufklapper: wann das Dokument nötig ist und was der SiGeKo damit tut. */
const DokumentErklaerung: React.FC<{ dokument: MitgeltendesDokument }> = ({ dokument }) => (
  <dl className="mt-1.5 grid gap-1.5 text-xs text-slate-600 bg-white border border-slate-200 rounded p-2">
    <ErklaerungsZeile titel="Wann benötigt">{dokument.wannBenoetigt}</ErklaerungsZeile>
    <ErklaerungsZeile titel="Für den SiGeKo">{dokument.sigekoRelevanz}</ErklaerungsZeile>
    {dokument.vorlauf && <ErklaerungsZeile titel="Vorlauf">{dokument.vorlauf}</ErklaerungsZeile>}
    {!!dokument.rechtsgrundlage?.length && (
      <ErklaerungsZeile titel="Rechtsgrundlage">{dokument.rechtsgrundlage.join('; ')}</ErklaerungsZeile>
    )}
    {!!dokument.lieferant?.length && (
      <ErklaerungsZeile titel="Liefert">{dokument.lieferant.map(l => LIEFERANT_TITEL[l] || l).join(', ')}</ErklaerungsZeile>
    )}
    <ErklaerungsZeile titel="Art">{`${dokument.art} – ${ART_TITEL[dokument.art]}`}</ErklaerungsZeile>
    <ErklaerungsZeile titel="Beleg" className={dokument.beleg === 'praxis' ? 'text-amber-700' : ''}>
      {dokument.beleg === 'MK'
        ? 'Im Mängelkatalog mit Rechtsgrundlage belegt'
        : 'Fachpraxis – vor Freigabe prüfen'}
    </ErklaerungsZeile>
  </dl>
);

const InfoKnopf: React.FC<{ offen: boolean; onClick: () => void }> = ({ offen, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-expanded={offen}
    title={offen ? 'Erklärung schließen' : 'Wann benötigt? Was tut der SiGeKo damit?'}
    className={`shrink-0 p-1.5 -my-1 rounded hover:bg-blue-50 ${offen ? 'text-blue-600' : 'text-slate-400 hover:text-blue-600'}`}
  >
    <Info className="w-3.5 h-3.5" />
  </button>
);

export const MitgeltendeDokumenteEditor: React.FC<MitgeltendeDokumenteEditorProps> = ({
  dokumente, onChange, schritt, titel = 'Mitgeltende Dokumente', icon, darstellung = 'feld', vorbelegt,
}) => {
  const eingabeId = useId();
  const [draft, setDraft] = useState('');
  const [katalogOffen, setKatalogOffen] = useState(false);
  // Welche Erklärung aufgeklappt ist: `d:<Name>` (eingetragen) oder `v:<Id>` (Vorschlag).
  const [offen, setOffen] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);

  const { bezeichnung, gewerk, schutzmassnahmen, vorschriften } = schritt;
  const vorschlaege = useMemo(
    () => vorschlaegeFuerSchritt({ bezeichnung, gewerk, schutzmassnahmen, vorschriften, mitgeltende_dokumente: dokumente }),
    [bezeichnung, gewerk, schutzmassnahmen, vorschriften, dokumente],
  );

  const vorhanden = useMemo(() => new Set(dokumente.map(dokumentSchluessel)), [dokumente]);
  const vorbelegtSchluessel = useMemo(() => new Set((vorbelegt || []).map(dokumentSchluessel)), [vorbelegt]);

  const suchtreffer = useMemo(() => {
    const q = normalisiere(draft);
    if (!q) return [];
    return AUSWAHL
      .filter(d => [d.name, d.kurz, ...(d.alias || [])].some(t => !!t && normalisiere(t).includes(q)))
      .slice(0, 12);
  }, [draft]);

  const katalogGruppen = useMemo(
    () => (Object.keys(GRUPPEN_TITEL) as DokumentGruppe[])
      .map(g => ({ gruppe: g, dokumente: AUSWAHL.filter(d => d.gruppe === g) }))
      .filter(g => g.dokumente.length > 0),
    [],
  );

  const umschalten = (schluessel: string) => setOffen(o => (o === schluessel ? null : schluessel));

  const hinzufuegen = (roh: string) => {
    const text = roh.trim();
    if (!text) return;
    const dok = findeDokument(text);
    if (dok?.art === 'C') {
      setMeldung(`„${dok.name}“ ist eine allgemeine Pflicht des Arbeitgebers und steht im Plan einmal als Grundsatz – nicht je Arbeitsschritt.`);
      return;
    }
    const name = dok ? dok.name : text;
    const neu = ergaenzeDokumente(dokumente, [name]);
    if (neu.length === dokumente.length) {
      setMeldung(`„${name}“ ist bereits eingetragen.`);
    } else {
      onChange(neu);
      setMeldung(null);
    }
    setDraft('');
  };

  const entfernen = (index: number) => {
    onChange(dokumente.filter((_, j) => j !== index));
    setMeldung(null);
  };

  // „Alle übernehmen“ nimmt nur Vorschläge ohne Bedingung — ob „nur im
  // Bestand“ zutrifft, entscheidet der Bearbeiter je Vorschlag.
  const unbedingt = vorschlaege.filter(v => !istBedingt(v));
  const hatBedingte = unbedingt.length < vorschlaege.length;
  const alleUebernehmen = () => {
    onChange(ergaenzeDokumente(dokumente, unbedingt.map(v => v.dokument.name)));
    setMeldung(null);
  };

  const trefferZeile = (d: MitgeltendesDokument) => {
    const schonDa = vorhanden.has(dokumentSchluessel(d.name));
    return (
      <button
        key={d.id}
        type="button"
        disabled={schonDa}
        onClick={() => hinzufuegen(d.name)}
        title={d.wannBenoetigt}
        className="w-full text-left px-2 py-2 flex items-start gap-2 text-sm hover:bg-blue-50 disabled:hover:bg-transparent disabled:text-slate-400"
      >
        <ArtMarke art={d.art} />
        <span className="flex-1 min-w-0 break-words">
          {d.name}
          {d.kurz && <span className="ml-1.5 text-[11px] text-slate-400">({d.kurz})</span>}
        </span>
        {schonDa
          ? <span className="shrink-0 inline-flex items-center gap-0.5 text-[11px] text-green-700"><Check className="w-3 h-3" />eingetragen</span>
          : <Plus className="shrink-0 w-3.5 h-3.5 mt-0.5 text-slate-400" />}
      </button>
    );
  };

  const zeigeTreffer = !!draft.trim() && suchtreffer.length > 0;
  const zeigeKatalog = katalogOffen && !draft.trim();

  const inhalt = (
    <>
      {darstellung === 'karte' ? (
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-3">
          {icon}
          <label htmlFor={eingabeId}>{titel}</label>
          <span className="ml-auto text-xs font-normal text-slate-400">{dokumente.length}</span>
        </div>
      ) : (
        <label htmlFor={eingabeId} className="block text-xs text-slate-500 mb-1">{titel}</label>
      )}

      <div className="space-y-1 mb-2">
        {dokumente.map((name, i) => {
          const dok = findeDokument(name);
          const schluessel = `d:${name}`;
          return (
            <div key={`${name}-${i}`} className="text-sm bg-slate-50 border border-slate-200 rounded px-2 py-1">
              <div className="flex items-start gap-2">
                <ArtMarke art={dok?.art} />
                <span className="flex-1 min-w-0 break-words py-0.5">
                  {name}
                  {dok?.kurz && dok.kurz !== name && <span className="ml-1.5 text-[11px] text-slate-400">({dok.kurz})</span>}
                  {vorbelegtSchluessel.has(dokumentSchluessel(name)) && (
                    <span className="ml-1.5 text-[10px] font-medium text-indigo-700 bg-indigo-50 border border-indigo-100 rounded px-1 py-px align-middle">
                      vorgeschlagen
                    </span>
                  )}
                </span>
                {dok && <InfoKnopf offen={offen === schluessel} onClick={() => umschalten(schluessel)} />}
                <button
                  type="button"
                  onClick={() => entfernen(i)}
                  title="Entfernen"
                  className="shrink-0 p-1.5 -my-1 rounded text-slate-400 hover:text-red-500 hover:bg-red-50"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
              {dok && offen === schluessel && <DokumentErklaerung dokument={dok} />}
            </div>
          );
        })}
        {dokumente.length === 0 && <div className="text-xs text-slate-400 italic">Keine Einträge.</div>}
      </div>

      <div className="flex gap-2">
        <div className="relative flex-1 min-w-0">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <input
            id={eingabeId}
            className="w-full text-sm p-2 pl-8 border border-slate-200 rounded outline-none focus:ring-2 focus:ring-blue-500/20"
            placeholder="Dokument suchen oder frei eingeben…"
            value={draft}
            autoComplete="off"
            onChange={e => { setDraft(e.target.value); setMeldung(null); }}
            onKeyDown={e => {
              if (e.key === 'Enter') { e.preventDefault(); hinzufuegen(draft); }
              if (e.key === 'Escape') { setDraft(''); setKatalogOffen(false); }
            }}
          />
        </div>
        <Button variant="outline" size="sm" onClick={() => hinzufuegen(draft)} disabled={!draft.trim()} title="Eintrag übernehmen">
          <Plus className="w-4 h-4" />
        </Button>
        {AUSWAHL.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setKatalogOffen(o => !o)}
            aria-expanded={katalogOffen}
            title="Katalog der mitgeltenden Dokumente anzeigen"
            className={katalogOffen ? 'bg-blue-50 border-blue-300 text-blue-700' : ''}
          >
            <BookOpen className="w-4 h-4" />
            <span className="hidden sm:inline">Katalog</span>
          </Button>
        )}
      </div>
      {meldung && <p className="mt-1 text-[11px] text-amber-700">{meldung}</p>}

      {zeigeTreffer && (
        <div className="mt-1 border border-slate-200 rounded-lg bg-white max-h-64 overflow-y-auto divide-y divide-slate-100 shadow-sm">
          {suchtreffer.map(trefferZeile)}
        </div>
      )}

      {zeigeKatalog && (
        <div className="mt-1 border border-slate-200 rounded-lg bg-white max-h-72 overflow-y-auto shadow-sm">
          {katalogGruppen.map(g => (
            <div key={g.gruppe}>
              <div className="sticky top-0 bg-slate-50 border-b border-slate-100 px-2 py-1 text-[11px] font-semibold text-slate-500 uppercase tracking-wide">
                {GRUPPEN_TITEL[g.gruppe]}
              </div>
              <div className="divide-y divide-slate-100">{g.dokumente.map(trefferZeile)}</div>
            </div>
          ))}
        </div>
      )}

      {vorschlaege.length > 0 ? (
        <div className="mt-3 border border-indigo-100 bg-indigo-50/50 rounded-lg p-2.5">
          <div className="flex flex-wrap items-center gap-2 mb-1.5">
            <Lightbulb className="w-3.5 h-3.5 text-indigo-500" />
            <span className="text-xs font-semibold text-indigo-900">Vorschläge zu diesem Schritt</span>
            {(unbedingt.length > 1 || (unbedingt.length === 1 && hatBedingte)) && (
              <button
                type="button"
                onClick={alleUebernehmen}
                className="ml-auto text-[11px] font-medium text-indigo-700 hover:text-indigo-900 hover:bg-indigo-100 rounded px-1.5 py-1"
              >
                {hatBedingte
                  ? `Alle ohne Bedingung übernehmen (${unbedingt.length})`
                  : `Alle übernehmen (${unbedingt.length})`}
              </button>
            )}
          </div>
          <div className="space-y-1">
            {vorschlaege.map(v => {
              const schluessel = `v:${v.dokument.id}`;
              return (
                <div key={v.dokument.id} className="bg-white border border-indigo-100 rounded px-2 py-1.5">
                  <div className="flex items-start gap-2">
                    <ArtMarke art={v.dokument.art} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-slate-800 break-words">
                        {v.dokument.name}
                        {v.dokument.kurz && <span className="ml-1.5 text-[11px] text-slate-400">({v.dokument.kurz})</span>}
                      </div>
                      {v.bedingungen.map(b => (
                        <div key={b} className="text-[11px] text-amber-700">{bedingungsHinweis(b)}</div>
                      ))}
                    </div>
                    <InfoKnopf offen={offen === schluessel} onClick={() => umschalten(schluessel)} />
                    <Button variant="outline" size="sm" onClick={() => hinzufuegen(v.dokument.name)} title="Diesen Vorschlag übernehmen" className="shrink-0">
                      <Plus className="w-3.5 h-3.5" />
                      Übernehmen
                    </Button>
                  </div>
                  {offen === schluessel && <DokumentErklaerung dokument={v.dokument} />}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        bezeichnung?.trim() && (
          <p className="mt-2 text-[11px] text-slate-400">Keine weiteren Vorschläge aus dem Katalog zu diesem Schritt.</p>
        )
      )}
    </>
  );

  return darstellung === 'karte'
    ? <div className="bg-white border border-slate-200 rounded-xl p-4">{inhalt}</div>
    : <div>{inhalt}</div>;
};
